import { z } from 'zod';

import { defineRoute } from '../http/route-contract';
import { IsoDateTimeSchema } from '../primitives/datetime';
import { ChannelIdSchema, SubscriptionIdSchema } from '../primitives/ids';
import { ReasonCodeSchema } from '../reason-code';

/** Channel states of FXM-02 §5: never configured, active, paused, suspended by a limit or by an external problem. */
export const SubscriptionStatusSchema = z.enum([
  'NOT_CONFIGURED',
  'ACTIVE',
  'PAUSED',
  'SUSPENDED_LIMIT',
  'SUSPENDED_EXTERNAL',
]);
/** Subscription status. */
export type SubscriptionStatus = z.infer<typeof SubscriptionStatusSchema>;

/**
 * The causes each suspended status may carry (C1 §5.2 B-D6; M2 §4, M3 §4). Any other status has
 * none. Success bodies are strict (C1 R16): a new cause is a linked change in both repositories.
 */
const SUSPENSION_CAUSES: ReadonlyMap<SubscriptionStatus, ReadonlySet<string>> = new Map([
  ['SUSPENDED_LIMIT', new Set(['DAILY_LOSS_LIMIT_REACHED', 'MAX_POSITIONS_REACHED'])],
  [
    'SUSPENDED_EXTERNAL',
    new Set(['CHANNEL_INACCESSIBLE', 'TRADING_ACCOUNT_UNREACHABLE', 'TELEGRAM_SESSION_LOST']),
  ],
]);

/**
 * Cause of a suspension (C1 §5.2, B-D6, answers Q100): non-null exactly when the status is
 * `SUSPENDED_LIMIT` (`DAILY_LOSS_LIMIT_REACHED`, `MAX_POSITIONS_REACHED`) or `SUSPENDED_EXTERNAL`
 * (`CHANNEL_INACCESSIBLE`, `TRADING_ACCOUNT_UNREACHABLE`, `TELEGRAM_SESSION_LOST`). Only these five
 * codes, as the API declares them (A-G5); E7 shows their C2 text (I12).
 */
export const SuspensionReasonCodeSchema = ReasonCodeSchema.extract([
  'DAILY_LOSS_LIMIT_REACHED',
  'MAX_POSITIONS_REACHED',
  'CHANNEL_INACCESSIBLE',
  'TRADING_ACCOUNT_UNREACHABLE',
  'TELEGRAM_SESSION_LOST',
])
  .nullable()
  .describe(
    'Non-null exactly when status is SUSPENDED_LIMIT (DAILY_LOSS_LIMIT_REACHED or MAX_POSITIONS_REACHED) or SUSPENDED_EXTERNAL (CHANNEL_INACCESSIBLE, TRADING_ACCOUNT_UNREACHABLE or TELEGRAM_SESSION_LOST).',
  );

/**
 * Refinement shared by every object that carries a status and its suspension cause (C1 AC22):
 * a suspended status needs one of its own causes ({@link SUSPENSION_CAUSES}), any other status
 * has none.
 */
export function checkSuspensionCause(
  value: { readonly status: SubscriptionStatus; readonly suspensionReasonCode: string | null },
  ctx: z.RefinementCtx,
): void {
  const causes = SUSPENSION_CAUSES.get(value.status);
  const cause = value.suspensionReasonCode;
  let message: string | null = null;
  if (causes === undefined && cause !== null) {
    message = 'suspensionReasonCode is set only on a suspended status (C1 AC22)';
  } else if (causes !== undefined && cause === null) {
    message = 'A suspended status needs its suspensionReasonCode (C1 AC22)';
  } else if (causes !== undefined && cause !== null && !causes.has(cause)) {
    message = `${cause} is not a cause of ${value.status} (C1 §5.2)`;
  }
  if (message !== null) ctx.addIssue({ code: 'custom', path: ['suspensionReasonCode'], message });
}

/**
 * Subscription as embedded in a channel row. `suspensionReasonCode` follows the same rule as on
 * {@link SubscriptionSchema} (C1 §5.2, Proposal, to be validated): E12 names the cause from
 * `GET /channels` without a second call to `/me`.
 */
export const SubscriptionSummarySchema = z
  .object({
    id: SubscriptionIdSchema,
    status: SubscriptionStatusSchema,
    suspensionReasonCode: SuspensionReasonCodeSchema,
  })
  .superRefine(checkSuspensionCause);
/** Subscription summary. */
export type SubscriptionSummary = z.infer<typeof SubscriptionSummarySchema>;

/** A trader's subscription to one channel. */
export const SubscriptionSchema = z
  .object({
    id: SubscriptionIdSchema,
    channelId: ChannelIdSchema,
    status: SubscriptionStatusSchema,
    createdAt: IsoDateTimeSchema,
    suspensionReasonCode: SuspensionReasonCodeSchema,
  })
  .superRefine(checkSuspensionCause);
/** Subscription. */
export type Subscription = z.infer<typeof SubscriptionSchema>;

/** Body of POST /subscriptions. A new subscription is NOT_CONFIGURED: no copy before settings (I7). */
export const CreateSubscriptionRequestSchema = z.strictObject({ channelId: ChannelIdSchema });
/** POST /subscriptions body. */
export type CreateSubscriptionRequest = z.infer<typeof CreateSubscriptionRequestSchema>;

/** Path parameters of every /subscriptions/{subscriptionId} route. */
export const SubscriptionPathParamsSchema = z.strictObject({
  subscriptionId: SubscriptionIdSchema,
});

/** Response of POST /subscriptions/{id}/pause (FXM-02 §6 case 4). */
export const PauseSubscriptionResponseSchema = z.object({
  subscription: SubscriptionSchema,
  cancelledPendingValidations: z
    .int()
    .min(0)
    .describe(
      'Pending validation requests cancelled by the pause; where they are shown depends on Q25.',
    ),
  changed: z
    .boolean()
    .describe('false: the channel was already paused; nothing was written (M3 R6, B-D6).'),
});
/** Pause response. */
export type PauseSubscriptionResponse = z.infer<typeof PauseSubscriptionResponseSchema>;

/**
 * Response of POST /subscriptions/{id}/resume (C1 §5.2, B-D6). The status may stay suspended when a
 * limit or an external problem still holds (M3 R7): the trader's pause only is cleared.
 */
export const ResumeSubscriptionResponseSchema = z.object({
  subscription: SubscriptionSchema,
  changed: z
    .boolean()
    .describe('false: the channel was not paused; nothing was written (M3 R6, B-D6).'),
});
/** Resume response. */
export type ResumeSubscriptionResponse = z.infer<typeof ResumeSubscriptionResponseSchema>;

const subscription = { name: 'Subscription', schema: SubscriptionSchema } as const;
const ENGINE_TABLES = 'a subscription is a social_trading_investors row (RFX analysis §8).';

/** POST /subscriptions. */
export const createSubscriptionRoute = defineRoute({
  operationId: 'createSubscription',
  method: 'POST',
  path: '/subscriptions',
  tag: 'Subscriptions',
  summary: 'Select a channel to copy',
  description:
    'Creates a NOT_CONFIGURED subscription; nothing is copied before its settings are validated (FXM-02 §7). 409 TELEGRAM_SESSION_LOST and 503 TELEGRAM_UNAVAILABLE when the membership check cannot read the Telegram session of the trader (S5 R18).',
  source: 'technical spec §6; FXM-01 §4; US-04, US-07',
  auth: 'bearer',
  provisional: { questions: ['Q36', 'Q39'], note: ENGINE_TABLES },
  body: { name: 'CreateSubscriptionRequest', schema: CreateSubscriptionRequestSchema },
  success: { kind: 'json', status: 201, body: subscription },
  errors: [
    { status: 404, reasonCode: 'NOT_FOUND' },
    { status: 409, reasonCode: 'SUBSCRIPTION_ALREADY_EXISTS' },
    // S5 R18 (Proposal, to be validated): the membership check reads the trader's Telegram
    // session, as GET /channels does (A-D2).
    { status: 409, reasonCode: 'TELEGRAM_SESSION_LOST' },
    { status: 422, reasonCode: 'CHANNEL_INACCESSIBLE' },
    { status: 503, reasonCode: 'TELEGRAM_UNAVAILABLE' },
  ],
});

/** DELETE /subscriptions/{subscriptionId}. */
export const deleteSubscriptionRoute = defineRoute({
  operationId: 'deleteSubscription',
  method: 'DELETE',
  path: '/subscriptions/{subscriptionId}',
  tag: 'Subscriptions',
  summary: 'Remove a channel',
  description: 'Stops the next signals of the channel; open positions stay tracked (FXM-01 §4).',
  source: 'technical spec §6; FXM-01 §4; US-04, US-07',
  auth: 'bearer',
  provisional: { questions: ['Q36', 'Q39'], note: ENGINE_TABLES },
  params: SubscriptionPathParamsSchema,
  success: { kind: 'empty', status: 204 },
});

/** POST /subscriptions/{subscriptionId}/pause. */
export const pauseSubscriptionRoute = defineRoute({
  operationId: 'pauseSubscription',
  method: 'POST',
  path: '/subscriptions/{subscriptionId}/pause',
  tag: 'Subscriptions',
  summary: 'Pause a channel',
  description:
    'New signals are ignored, open positions stay tracked, and a pending validation is cancelled (FXM-02 §4, §6 case 4).',
  source: 'technical spec §6; FXM-02 §4; US-13',
  auth: 'bearer',
  provisional: {
    questions: ['Q25', 'Q39'],
    note: 'pause = status change in social_trading_investors.',
  },
  params: SubscriptionPathParamsSchema,
  success: {
    kind: 'json',
    status: 200,
    body: { name: 'PauseSubscriptionResponse', schema: PauseSubscriptionResponseSchema },
  },
  // 503 SERVICE_UNAVAILABLE is standard on every bearer route (C1 R9, M-G0); E7 shows it (M3 AC9).
});

/** POST /subscriptions/{subscriptionId}/resume. */
export const resumeSubscriptionRoute = defineRoute({
  operationId: 'resumeSubscription',
  method: 'POST',
  path: '/subscriptions/{subscriptionId}/resume',
  tag: 'Subscriptions',
  summary: 'Resume a paused channel',
  description:
    "Ends the trader's pause, for new signals only; a limit or external problem still holding keeps the channel suspended (M3 R4, R7). Refused without confirmed settings (M3 R5).",
  source: 'technical spec §6; system design §Mini App; FXM-02 §4, §5, §7; US-13',
  auth: 'bearer',
  provisional: { questions: ['Q39'], note: 'resume = status change in social_trading_investors.' },
  params: SubscriptionPathParamsSchema,
  success: {
    kind: 'json',
    status: 200,
    body: { name: 'ResumeSubscriptionResponse', schema: ResumeSubscriptionResponseSchema },
  },
  errors: [{ status: 409, reasonCode: 'CHANNEL_NOT_CONFIGURED' }],
});
